import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';

import { useTemplateStore } from '@/stores/template-store';
import { useUserProfileStore } from '@/stores/user-profile-store';
import { db } from '@/services/storage/db';
import { seedBuiltInTemplates } from '@/services/template-seed-service';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import type { ChecklistTemplate } from '@/types/checklist-template';
import { SeasonType } from '@/types/season';

import noSeasonsPng from '@/assets/illustrations/empty/no-seasons.png';

/** The one template Starter users can access for free. */
const FREE_TEMPLATE_ID = 'gun-season-midwest-v1';

type SeasonTypeFilter = 'all' | SeasonType;

const SEASON_TYPE_FILTERS: { value: SeasonTypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: SeasonType.Gun, label: 'Gun' },
  { value: SeasonType.Bow, label: 'Bow' },
  { value: SeasonType.Muzzleloader, label: 'Muzzleloader' },
];

const SEASON_TYPE_BADGE: Record<string, string> = {
  gun: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)] ring-1 ring-[var(--color-accent-pressed)]/40',
  bow: 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30',
  muzzleloader: 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]',
  crossbow: 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]',
  default: 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]',
};

function seasonTypeLabel(w: string): string {
  const map: Record<string, string> = {
    gun: 'Gun', bow: 'Bow', muzzleloader: 'Muzzleloader', crossbow: 'Crossbow',
    youth: 'Youth', other: 'Other',
  };
  return map[w] ?? w;
}

function TemplateCard({
  template,
  locked = false,
  onLockedTap,
  seasonQuery = '',
}: {
  template: ChecklistTemplate;
  locked?: boolean;
  onLockedTap?: () => void;
  /** M97 / #706: `?season=<id>` suffix carried through to the detail page so the
   *  chosen template attaches to the preselected populated season. */
  seasonQuery?: string;
}) {
  const badgeClass = SEASON_TYPE_BADGE[template.seasonType] ?? SEASON_TYPE_BADGE.default;

  if (locked) {
    return (
      <button
        type="button"
        data-testid="template-card"
        data-testid-locked="true"
        onClick={onLockedTap}
        className="relative block w-full text-left bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] rounded-lg shadow-card p-4 opacity-60 cursor-pointer"
        aria-label={`${template.name} — Pro feature`}
      >
        <div className="absolute inset-0 flex items-center justify-center rounded-lg">
          <span
            data-testid="template-lock-icon"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--color-elevated)] border border-[var(--color-border)]"
          >
            <Lock size={14} className="text-[var(--color-text-muted)]" />
          </span>
        </div>
        <div className="flex items-start justify-between gap-3 blur-[2px] select-none">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="font-display text-display-sm text-[var(--color-text-display)] line-clamp-1">
              {template.name}
            </span>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium flex-shrink-0 ${badgeClass}`}>
            {seasonTypeLabel(template.seasonType)}
          </span>
        </div>
      </button>
    );
  }

  return (
    <Link
      to={`/prep/templates/${template.id}${seasonQuery}`}
      data-testid="template-card"
      className="block bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] rounded-lg shadow-card p-4 hover:border-[var(--color-border-subtle)] hover:bg-[var(--color-elevated)] transition-colors duration-100"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <span
            className="font-display text-display-sm text-[var(--color-text-display)] line-clamp-1"
            data-typography="display-sm"
          >
            {template.name}
          </span>
          {template.description && (
            <p className="text-body-sm text-[var(--color-text-muted)] line-clamp-2">
              {template.description}
            </p>
          )}
        </div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium flex-shrink-0 ${badgeClass}`}
        >
          {seasonTypeLabel(template.seasonType)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-body-xs text-[var(--color-text-muted)] capitalize">{template.region}</span>
        <span className="text-[var(--color-border)]">·</span>
        <span
          data-testid="template-task-count"
          className="text-body-xs text-[var(--color-text-muted)]"
        >
          {template.tasks.length} tasks
        </span>
        {template.isBuiltIn && (
          <>
            <span className="text-[var(--color-border)]">·</span>
            <span className="text-body-xs text-[var(--color-accent)]">Built-in</span>
          </>
        )}
        {!template.isBuiltIn && (
          <>
            <span className="text-[var(--color-border)]">·</span>
            <Link
              to={`/prep/templates/${template.id}/edit`}
              data-testid="edit-template-link"
              onClick={(e) => e.stopPropagation()}
              className="text-body-xs text-[var(--color-accent)] hover:underline"
            >
              Edit
            </Link>
          </>
        )}
      </div>
    </Link>
  );
}

export function TemplatesPage() {
  const { templates, isLoading, loadTemplates } = useTemplateStore();
  const isPro = useUserProfileStore((s) => s.isPro);
  const [seasonTypeFilter, setSeasonTypeFilter] = useState<SeasonTypeFilter>('all');
  const [isSeeding, setIsSeeding] = useState(false);
  const [showTemplateGate, setShowTemplateGate] = useState(false);
  // M97 / #706: preserve the season preselect param across the card navigation so
  // the chosen template attaches to that season (append path) on the detail page.
  const [searchParams] = useSearchParams();
  const seasonParam = searchParams.get('season');
  const seasonQuery = seasonParam ? `?season=${seasonParam}` : '';

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  async function handleSeedTemplates() {
    setIsSeeding(true);
    try {
      await seedBuiltInTemplates(db);
      await loadTemplates();
    } catch (err) {
      console.error('TemplatesPage.handleSeedTemplates:', err);
    } finally {
      setIsSeeding(false);
    }
  }

  const filtered =
    seasonTypeFilter === 'all'
      ? templates
      : templates.filter((t) => t.seasonType === seasonTypeFilter);

  const sorted = [...filtered].sort((a, b) => {
    if (a.isBuiltIn !== b.isBuiltIn) return a.isBuiltIn ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-[var(--color-text-muted)] text-body-sm">Loading templates...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-display-lg text-[var(--color-text-display)]">Templates</h1>
          <p className="text-body-sm text-[var(--color-text-muted)] mt-1">
            Checklist templates for seasonal prep
          </p>
        </div>
        <Link
          to="/prep/templates/new"
          data-testid="create-template-btn"
          className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2.5 rounded-md text-body-sm font-medium bg-[var(--color-accent)] text-[var(--color-text-display)] hover:bg-[var(--color-accent-hover)] transition-colors"
        >
          + Create Template
        </Link>
      </div>

      {/* Season type filter chips */}
      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by season type">
        {SEASON_TYPE_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setSeasonTypeFilter(value)}
            data-testid={`filter-${value}`}
            className={`px-3 py-1.5 rounded-full text-body-xs font-medium transition-colors duration-100 ${
              seasonTypeFilter === value
                ? 'bg-[var(--color-accent)] text-[var(--color-text-display)]'
                : 'bg-[var(--color-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        seasonTypeFilter === 'all' ? (
          <OperationalGuide
            illustration={noSeasonsPng}
            heading="No Templates Yet"
            subtext="Create a template to generate task checklists for future seasons."
            ctaLabel={isSeeding ? 'Loading…' : 'Load default templates'}
            onCtaClick={() => void handleSeedTemplates()}
            data-guide-id="templates-empty"
          />
        ) : (
          <div
            data-testid="empty-state"
            className="flex flex-col items-center justify-center py-16 px-6 text-center"
          >
            <p className="font-display text-display-sm text-[var(--color-text-primary)] mb-2">No Templates Yet</p>
            <p className="text-body-sm text-[var(--color-text-muted)] mb-6 max-w-xs">
              {`No ${seasonTypeLabel(seasonTypeFilter)} templates available yet.`}
            </p>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((template) => {
            const isLocked = !isPro() && template.id !== FREE_TEMPLATE_ID;
            return (
              <TemplateCard
                key={template.id}
                template={template}
                locked={isLocked}
                onLockedTap={() => setShowTemplateGate(true)}
                seasonQuery={seasonQuery}
              />
            );
          })}
        </div>
      )}

      {showTemplateGate && (
        <UpsellSheet
          featureName={UPSELL_COPY.template_library.featureName}
          description={UPSELL_COPY.template_library.description}
          featureGate="template_library"
          onDismiss={() => setShowTemplateGate(false)}
        />
      )}
    </div>
  );
}
